import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useCardsStore } from '@/stores/cardsStore'
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
} from '@mui/material'
import Link from '@mui/material/Link'
import { Link as RouterLink } from 'react-router-dom'
import {
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  OpenInNew as GoToWordIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { WordAudioButton } from '@/components/words/WordAudioButton'
import { CardProgressDisplay } from '@/components/cards/CardProgress'
import { CardClues } from '@/components/cards/CardClues'
import { CardProgressHistory } from '@/components/cards/CardProgressHistory'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

export function CardViewPage() {
  const params = useParams<{ id?: string; cardId?: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const rawCardId = params.cardId ?? params.id
  const cardId = rawCardId ? parseInt(rawCardId, 10) : NaN
  const userId = params.cardId != null && params.id ? parseInt(params.id, 10) : undefined
  const isTeacherView = userId != null

  const user = useAuthStore((s) => s.user)
  const isTeacherOrAdmin = Boolean(
    user?.roles?.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  )
  const { currentCard, isLoading, error, fetchCard, clearError, deleteCard } = useCardsStore()
  const [clues, setClues] = useState(currentCard?.clues ?? [])
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    if (!isNaN(cardId)) {
      fetchCard(cardId).then((card) => {
        if (card?.clues) setClues(card.clues)
      })
    }
  }, [cardId, fetchCard])

  useEffect(() => {
    if (currentCard?.clues) setClues(currentCard.clues)
  }, [currentCard?.clues])

  const handleClueAdded = () => {
    if (!isNaN(cardId)) {
      fetchCard(cardId).then((card) => {
        if (card?.clues) setClues(card.clues)
      })
    }
  }

  const handleClueDeleted = () => {
    setClues((prev) => prev)
    if (!isNaN(cardId)) {
      fetchCard(cardId).then((card) => {
        if (card?.clues) setClues(card.clues)
      })
    }
  }

  const handleDeleteClick = async () => {
    setMenuAnchor(null)
    if (!currentCard) return
    if (!window.confirm(t('common.delete') + '?')) return
    setDeleteLoading(true)
    try {
      await deleteCard(currentCard.id)
      navigate(isTeacherView ? ROUTES.USER_CARDS(userId!) : ROUTES.CARDS)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleGoToWordClick = () => {
    setMenuAnchor(null)
    if (!currentCard?.word_id) return
    navigate(ROUTES.WORD_VIEW(currentCard.word_id))
  }

  if (isNaN(cardId)) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{t('cards.errors.load_failed')}</Alert>
        </Box>
      </DashboardLayout>
    )
  }

  if (isLoading && !currentCard) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Box>
      </DashboardLayout>
    )
  }

  if (!currentCard) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || t('cards.errors.load_failed')}</Alert>
          <Button
            onClick={() => navigate(isTeacherView ? ROUTES.USER_CARDS(userId!) : ROUTES.CARDS)}
            sx={{ mt: 2 }}
          >
            {isTeacherView ? t('cards.student_cards') : t('cards.my_cards')}
          </Button>
        </Box>
      </DashboardLayout>
    )
  }

  const word = currentCard.word
  // Играем по audio_url (абсолютный URL public-диска), а не по сырому пути атрибута
  const audioUrl = word?.audio_url ?? null

  const attributeLabels: Record<string, string> = {
    transcription: t('cards.transcription'),
    translation: t('cards.translation'),
    part_of_speech: t('cards.part_of_speech'),
    type: t('cards.word_type'),
    example_sentence: t('cards.word_attrs.example_sentence'),
    synonym: t('cards.word_attrs.synonym'),
    antonym: t('cards.word_attrs.antonym'),
    note: t('cards.word_attrs.note'),
    audio_path: t('cards.word_attrs.audio_path'),
  }
  const getAttrLabel = (key: string) => attributeLabels[key] ?? key.replace(/_/g, ' ')

  const cardsLink = isTeacherView ? ROUTES.USER_CARDS(userId!) : ROUTES.CARDS
  const cardsLabel = isTeacherView ? t('cards.student_cards') : t('cards.my_cards')

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={cardsLink} underline="hover">
            {cardsLabel}
          </Link>
          <Typography color="text.primary">{word?.text ?? '—'}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton
            onClick={() =>
              navigate(isTeacherView ? ROUTES.USER_CARDS(userId!) : ROUTES.CARDS)
            }
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {word?.text ?? '—'}
          </Typography>
          <PageHelpPopover pageKey={isTeacherView ? 'card-view' : 'card-view-student'} />
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={deleteLoading}
            aria-label={t('common.actions')}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'inherit' }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('cards.delete')}</ListItemText>
            </MenuItem>
            {isTeacherOrAdmin && (
              <MenuItem onClick={handleGoToWordClick}>
                <ListItemIcon>
                  <GoToWordIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('cards.go_to_word')}</ListItemText>
              </MenuItem>
            )}
          </Menu>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Card sx={{ flex: 1, minWidth: 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cards.word')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5">{word?.text ?? '—'}</Typography>
                <WordAudioButton audioUrl={audioUrl} size="medium" />
              </Box>
              {word?.attributes && word.attributes.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {word.attributes.map((attr) => (
                    <Box key={attr.key}>
                      <Typography variant="caption" color="text.secondary">
                        {getAttrLabel(attr.key)}
                      </Typography>
                      <Typography variant="body1">
                        {attr.key === 'audio_path' ? (attr.value ? t('cards.word_attrs.audio_path') : '—') : (attr.value || '—')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 0 }}>
            <CardContent>
              <CardProgressDisplay card={currentCard} />
            </CardContent>
          </Card>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <CardClues
              cardId={currentCard.id}
              wordId={currentCard.word_id}
              clues={clues}
              onClueAdded={handleClueAdded}
              onClueDeleted={handleClueDeleted}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <CardProgressHistory cardId={currentCard.id} />
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  )
}
